import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getRecordings, saveRecording, StoredRecording } from '@/lib/db'

type UseRecordingProps = {
  domain: string
  productId: string
  episodeId: string
}

type UseRecordingReturn = {
  isRecording: boolean
  recordingTime: number
  recordings: StoredRecording[]
  showRecordingsSheet: boolean
  setShowRecordingsSheet: (show: boolean) => void
  handleRecordClick: () => Promise<void>
  loadRecordings: () => Promise<void>
}

/**
 * 録音機能を管理するカスタムフック
 * MediaRecorder APIを使用して音声を録音し、IndexedDBに保存
 */
export const useRecording = ({ domain, productId, episodeId }: UseRecordingProps): UseRecordingReturn => {
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [recordings, setRecordings] = useState<StoredRecording[]>([])
  const [showRecordingsSheet, setShowRecordingsSheet] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recordingStartTimeRef = useRef<number>(0)

  // 録音リストを取得
  const loadRecordings = useCallback(async () => {
    try {
      const recs = await getRecordings(domain, productId, episodeId)
      setRecordings(recs)
    } catch (error) {
      console.error('Failed to load recordings:', error)
    }
  }, [domain, productId, episodeId])

  // 録音の開始/停止
  const handleRecordClick = useCallback(async () => {
    if (isRecording) {
      // 録音停止
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
        recordingTimerRef.current = null
      }
      setIsRecording(false)
      return
    }

    // 録音開始 - ユーザーインタラクションから即座にgetUserMediaを呼び出す
    let stream: MediaStream | null = null

    try {
      // iOSの制約に対応するため、ユーザーインタラクション直後にgetUserMediaを呼び出す
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          toast.error('マイクへのアクセスが拒否されました\n設定からマイクのアクセスを許可してください')
          return
        }
        if (error.name === 'NotFoundError') {
          toast.error('マイクが見つかりませんでした')
          return
        }
        toast.error('マイクへのアクセスに失敗しました')
        return
      }

      toast.error('マイクへのアクセスに失敗しました')
      return
    }

    // MediaRecorderのセットアップ
    try {
      // サポートされているMIMEタイプを選択
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : 'audio/wav'

      // MediaRecorderのセットアップ
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      recordingChunksRef.current = []
      recordingStartTimeRef.current = Date.now()
      setRecordingTime(0)

      // オーディオトラックの状態チェック
      const audioTracks = stream.getAudioTracks()
      if (audioTracks.length === 0) {
        toast.error('オーディオトラックが見つかりませんでした')
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      // オーディオトラックが既に終了していないか確認
      const deadTracks = audioTracks.filter((track) => track.readyState === 'ended')
      if (deadTracks.length > 0) {
        console.error('Dead audio tracks found:', deadTracks)
        toast.error('オーディオトラックが無効です')
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      // オーディオトラック終了時のエラーハンドリング
      audioTracks.forEach((track) => {
        track.addEventListener('ended', () => {
          console.error('Audio track ended unexpectedly')
          toast.error('録音が予期せず終了しました')
          setIsRecording(false)
        })
      })

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
        toast.error('録音中にエラーが発生しました')
      }

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(recordingChunksRef.current, { type: mimeType })

        // ストリームを停止
        stream?.getTracks().forEach((track) => track.stop())

        try {
          await saveRecording(domain, productId, episodeId, audioBlob, recordingTime / 1000)
          toast.success(`録音を保存しました (${(recordingTime / 1000).toFixed(1)}秒)`)
          await loadRecordings()
          // 録音一覧シートを開く
          setShowRecordingsSheet(true)
        } catch (error) {
          console.error('Failed to save recording:', error)
          toast.error('録音の保存に失敗しました')
        }
      }

      // 録音開始
      mediaRecorder.start()
      setIsRecording(true)

      // タイマー開始（既存のタイマーがあればクリア）
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }

      recordingTimerRef.current = setInterval(() => {
        const now = Date.now()
        const elapsed = now - recordingStartTimeRef.current
        setRecordingTime(elapsed)
      }, 100)
    } catch (error) {
      console.error('MediaRecorder setup error:', error)
      toast.error('録音の初期化に失敗しました')
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [isRecording, domain, productId, episodeId, recordingTime, loadRecordings])

  // 録音中のビジビリティ変更を監視
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        console.warn('⚠️ Page hidden while recording!')
        toast.warning('録音中にバックグラウンドに移行しました。\n' + '録音が正常に保存されない可能性があります。', {
          duration: 5000,
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isRecording])

  return {
    isRecording,
    recordingTime,
    recordings,
    showRecordingsSheet,
    setShowRecordingsSheet,
    handleRecordClick,
    loadRecordings,
  }
}
